import { AxiosResponse } from 'axios';
import ApiUrls from '../config/ApiUrls';
import { replaceByObject } from '../utils/Urls';
import API, { PageWithTotal } from './Api';
import { TaxRateApiResponse } from './EntityApi';
import { ExchangeRatesResponse } from './ExchangeRatesApi';
import { RuleTransactionType } from '../config/Constants';

export enum CalculationStatus {
  calculating = 'calculating',
  complete = 'complete',
  recalculating = 'recalculating',
  error = 'error',
}

export type CalculationApiResponse = {
  id: number;
  created: string;
  buildStatus: CalculationStatus;
  start: string;
  end: string;
  locked: boolean;
  lockDate: string;
  version: number;
  parent: number;
  matched: TransactionMatchApiResponse[];
  unmatched: TransactionApiResponse[];
  exchangeRates: ExchangeRatesResponse;
  postingSummaries: PostingSummaryApiResponse[];
  postingDate: string;
  hasSecondRecharges: boolean;
};

export enum NominalCodeType {
  INCOME = 'income',
  EXPENSE = 'expense',
  BALANCESHEET = 'balancesheet',
  ASSET = 'asset',
  EQUITY = 'equity',
  LIABILITY = 'liability',
}

export enum TransactionSource {
  ACCREC = 'ACCREC',
  ACCPAY = 'ACCPAY',
  ACCRECCREDIT = 'ACCRECCREDIT',
  ACCPAYCREDIT = 'ACCPAYCREDIT',
  ACCRECPAYMENT = 'ACCRECPAYMENT',
  ACCPAYPAYMENT = 'ACCPAYPAYMENT',
  ARCREDITPAYMENT = 'ARCREDITPAYMENT',
  APCREDITPAYMENT = 'APCREDITPAYMENT',
  CASHREC = 'CASHREC',
  CASHPAID = 'CASHPAID',
  TRANSFER = 'TRANSFER',
  ARPREPAYMENT = 'ARPREPAYMENT',
  APPREPAYMENT = 'APPREPAYMENT',
  AROVERPAYMENT = 'AROVERPAYMENT',
  APOVERPAYMENT = 'APOVERPAYMENT',
  EXPCLAIM = 'EXPCLAIM',
  EXPPAYMENT = 'EXPPAYMENT',
  MANJOURNAL = 'MANJOURNAL',
  PAYSLIP = 'PAYSLIP',
  WAGEPAYABLE = 'WAGEPAYABLE',
  INTEGRATEDPAYROLLPE = 'INTEGRATEDPAYROLLPE',
  INTEGRATEDPAYROLLPT = 'INTEGRATEDPAYROLLPT',
  EXTERNALSPENDMONEY = 'EXTERNALSPENDMONEY',
  INTEGRATEDPAYROLLPTPAYMENT = 'INTEGRATEDPAYROLLPTPAYMENT',
  INTEGRATEDPAYROLLCN = 'INTEGRATEDPAYROLLCN',
  RECHARGE = 'RECHARGE',
  SOURCE_LESS = 'SOURCE_LESS',
}

export type TransactionApiResponse = {
  id: number;
  organisationId: number;
  externalId: string;
  journalSourceId: string;
  date: string;
  created: string;
  amount: number;
  currency: string;
  nominalCode: string;
  trackingCategory: string;
  contact: string;
  contactLink: string;
  title: string;
  description: string;
  type: string;
  sourceType: string;
  source: string;
};

export type TransactionMatchApiResponse = {
  id: number;
  organisationId: number;
  transaction: TransactionApiResponse;
  detectedRuleApportionmentId: number;
  overrideRuleApportionmentId: number;
  doNotRecharge: boolean;
  rechargeValues: RechargeValueApiResponse[];
};

export type RechargeValueApiResponse = {
  apportionment: { id: number };
  organisationId?: number;
  value: number;
  nominalCode: string;
};

export type PostingSummaryApiResponse = {
  id: number;
  created?: string;
  postedAt?: string;
  from: number;
  to: number;
  externalId: string;
};

export type PostingApiResponse = PostingSummaryApiResponse & {
  invoiceType: string;
  lines: PostingLineApiResponse[];
  lineAmountType: string;
  type: string;
  state: string;
  xeroState: string;
  purpose: string;
  relatedEntityType: string;
  relatedEntityId: number;
  issueDate: string;
  dueDate: string;
  invoiceNumber: string;
  reference: string;
  currency: string;
  contactId: string;
  sourceUrl?: string;
  outdated?: boolean;
  exchangeRates?: ExchangeRatesResponse;
  paidToNominalCode?: string;
  grouping: string;
};

export type PostingLineApiResponse = {
  key: string;
  name: string;
  ruleApportionmentId: number;
  nominalCode: string;
  subtotal: number;
  taxAmount: number;
  taxRate: Partial<TaxRateApiResponse>;
  amount: number;
  trackingCategories: string[];
  currency: string;
  balancingLine: boolean;
};

export type BatchTransactionMatchUpdate = {
  matchIds: number[];
  doNotRecharge: boolean;
  ruleApportionmentId: number;
};

export class CalculationApi extends API {
  static getCalculation(
    groupId: number,
    calculationId: number
  ): Promise<CalculationApiResponse> {
    return API.get(replaceByObject(ApiUrls.Calculation, { groupId, calculationId })).then(
      (response: AxiosResponse<CalculationApiResponse>) => {
        return response.data;
      }
    );
  }

  static getLatestId(
    groupId: number,
    calculationId: number
  ): Promise<CalculationApiResponse> {
    return API.get(
      replaceByObject(ApiUrls.CalculationLatestId, { groupId, calculationId })
    ).then((response: AxiosResponse<CalculationApiResponse>) => response.data);
  }

  static getInvoices(
    groupId: number,
    calculationId: number,
    create: boolean
  ): Promise<PostingApiResponse[]> {
    return API.get(
      replaceByObject(ApiUrls.CalculationPostings, { groupId, calculationId }),
      {
        params: { create },
      }
    ).then((response: AxiosResponse<PostingApiResponse[]>) => {
      return response.data;
    });
  }

  static deleteCalculation(groupId: number, calculationId: number): Promise<void> {
    return API.delete(
      replaceByObject(ApiUrls.Calculation, { groupId, calculationId })
    ).then((response: AxiosResponse<void>) => {
      return response.data;
    });
  }

  static getGroup(groupId: number): Promise<CalculationApiResponse[]> {
    return API.get(replaceByObject(ApiUrls.GroupCalculations, { groupId })).then(
      (response: AxiosResponse<CalculationApiResponse[]>) => {
        return response.data;
      }
    );
  }

  static create(
    groupId: number,
    start: string,
    end: string
  ): Promise<CalculationApiResponse> {
    return API.post(
      replaceByObject(ApiUrls.GroupCalculations, { groupId }),
      {},
      {
        params: { start, end },
      }
    ).then((response: AxiosResponse<CalculationApiResponse>) => {
      return response.data;
    });
  }

  static lock(groupId: number, calculationId: number): Promise<CalculationApiResponse> {
    return API.post(
      replaceByObject(ApiUrls.CalculationLock, { groupId, calculationId }),
      {}
    ).then((response: AxiosResponse<CalculationApiResponse>) => {
      return response.data;
    });
  }

  static unlock(groupId: number, calculationId: number): Promise<CalculationApiResponse> {
    return API.post(
      replaceByObject(ApiUrls.CalculationUnlock, { groupId, calculationId }),
      {}
    ).then((response: AxiosResponse<CalculationApiResponse>) => {
      return response.data;
    });
  }

  static recalculate(
    groupId: number,
    calculationId: number
  ): Promise<CalculationApiResponse> {
    return API.post(
      replaceByObject(ApiUrls.CalculationRecalculate, { groupId, calculationId }),
      {}
    ).then((response: AxiosResponse<CalculationApiResponse>) => {
      return response.data;
    });
  }

  static getPosting(
    groupId: number,
    calculationId: number,
    postingId: number
  ): Promise<PostingApiResponse> {
    return API.get(
      replaceByObject(ApiUrls.CalculationPosting, { groupId, calculationId, postingId })
    ).then((response: AxiosResponse<PostingApiResponse>) => {
      return response.data;
    });
  }

  static updatePosting(
    groupId: number,
    calculationId: number,
    postingId: number,
    posting: PostingApiResponse,
    abortController: AbortController
  ): Promise<PostingApiResponse> {
    return API.put(
      replaceByObject(ApiUrls.CalculationPosting, { groupId, calculationId, postingId }),
      posting,
      {
        signal: abortController.signal,
      }
    ).then((response: AxiosResponse<PostingApiResponse>) => {
      return response.data;
    });
  }

  static updatePostingAndSubmit(
    groupId: number,
    calculationId: number,
    postingId: number,
    posting: PostingApiResponse,
    abortController: AbortController
  ): Promise<PostingApiResponse> {
    return API.put(
      replaceByObject(ApiUrls.CalculationPosting, { groupId, calculationId, postingId }),
      posting,
      {
        params: {
          submit: true,
          markAsPaid: false,
        },
        signal: abortController.signal,
      }
    ).then((response: AxiosResponse<PostingApiResponse>) => {
      return response.data;
    });
  }

  static markAsPaid(
    groupId: number,
    calculationId: number,
    postingId: number,
    posting: PostingApiResponse,
    abortController: AbortController
  ): Promise<PostingApiResponse> {
    return API.put(
      replaceByObject(ApiUrls.CalculationPosting, { groupId, calculationId, postingId }),
      posting,
      {
        params: {
          submit: false,
          markAsPaid: true,
        },
        signal: abortController.signal,
      }
    ).then((response: AxiosResponse<PostingApiResponse>) => {
      return response.data;
    });
  }

  static recreatePosting(
    groupId: number,
    calculationId: number,
    postingId: number,
    posting: PostingApiResponse,
    abortController: AbortController
  ): Promise<PostingApiResponse> {
    return API.put(
      replaceByObject(ApiUrls.CalculationPosting, { groupId, calculationId, postingId }),
      posting,
      {
        params: {
          submit: true,
          recreate: true,
        },
        signal: abortController.signal,
      }
    ).then((response: AxiosResponse<PostingApiResponse>) => {
      return response.data;
    });
  }

  static batchUpdate(
    groupId: number,
    calculationId: number,
    updates: BatchTransactionMatchUpdate
  ): Promise<TransactionMatchApiResponse[]> {
    return API.post(
      replaceByObject(ApiUrls.BatchUpdateOverrideRule, { groupId, calculationId }),
      updates
    ).then((response: AxiosResponse<TransactionMatchApiResponse[]>) => {
      return response.data;
    });
  }

  static getUnmatched(
    groupId: number,
    calculationId: number,
    entityId: number,
    ruleTransactionType: RuleTransactionType,
    nominalCode: string,
    page: number
  ): Promise<PageWithTotal<TransactionApiResponse>> {
    const type = (() => {
      switch (ruleTransactionType) {
        case RuleTransactionType.expense: return 'expense';
        case RuleTransactionType.income: return 'income';
        case RuleTransactionType.balancesheet: return 'balancesheet';
      }
    })();

    return API.get(
      replaceByObject(ApiUrls.UnmatchedTransactions, { groupId, calculationId, entityId, type }),
      {
        params: {
          page,
          pageSize: 100,
          nominalCode,
        },
      }
    ).then((response: AxiosResponse<PageWithTotal<TransactionApiResponse>>) => {
      return response.data;
    });
  }
}
